<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col class="align-center d-flex" cols="10">
					<v-toolbar-title>{{ $t('add-medical-note-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer></v-spacer>
				<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
			</v-row>
		</v-card-title>

		<v-card-text class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						outlined
						v-model="performedBy"
						:label="$t('noting-staff')"
						:items="medicalStaff"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="selectedNoteTitleOption"
						:items="noteTitleOptions"
						item-value="value"
						item-text="value"
						:label="$t('note-title')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">
										{{ item.value }}
									</span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="selectedNoteTypeOption"
						:items="noteTypeOptions"
						item-value="value"
						item-text="value"
						:label="$t('note-type')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">
										{{ item.value }}
									</span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12">
					<v-textarea
						outlined
						dense
						v-model="noteContent"
						:label="$t('note-content')"
						hide-details
						no-resize
						row-height="45"
						rows="15" />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')"
						:dense="false" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip left v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addMedicalNote()" color="dark_purple" style="color: white" block>
						{{ $t('add') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { getRowsForDataTable } from '@/utils/utilities';
import { mapActions } from 'vuex/dist/vuex.common.js';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMMedicalNoteAdd',
	components: { InputTimeFormatter },
	data () {
		return {
			loading: false,
			medicalStaff: null,
			durationDays: 0,
			route: '',
			additionalData: '',
			timeSpentSeconds: 0,
			selectedHospital: null,
			performedBy: null,
			noteContent: '',
			noteTitleOptions: [],
			selectedNoteTitleOption: null,
			noteTypeOptions: [],
			selectedNoteTypeOption: null,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			detailedUser: (state) => state.users.detailedUser,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			roleList: (state) => state.roles.roles,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		textareaRows () {
			return this.$vuetify.breakpoint.lgAndUp ? 30 : 19;
		},
		headers () {
			return [
				{
					text: this.$t('prescription-brand-name'),
					value: 'brandName',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('dosage'),
					value: 'dosageInstructions',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('route'),
					value: 'route',
					class: 'table-header pl-2 pr-0',
					sortable: false,
				},
				{
					text: '',
					align: 'start pl-2 pr-0',
					sortable: false,
				},
			];
		},
		addButtonEnabled () {
			return (
				this.selectedNoteTypeOption &&
				this.selectedNoteTitleOption &&
				this.performedBy &&
				this.noteContent.trim() &&
				this.timeSpentSeconds &&
				this.timeSpentSeconds <= 7200
			);
		},
		hospitals () {
			const allHospitals = this.$store.state.hospitals.hospitals;
			return allHospitals;
		},
	},
	watch: {
		selectedPrescription (newPrescription) {
			if (newPrescription && newPrescription.dosageInstructions) {
				this.dosage = newPrescription.dosageInstructions;
			} else {
				this.dosage = '';
			}
		},
	},

	methods: {
		...mapActions({
			getMedicalNoteTitleOption: 'ccmRecords/getMedicalNoteTitleOption',
			getMedicalNoteTypeOption: 'ccmRecords/getMedicalNoteTypeOption',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),
		getRowsForDataTable,
		closeDrawer () {
			this.performedBy = null;
			this.selectedNoteTitleOption = null;
			this.selectedNoteTypeOption = null;
			this.noteContent = '';
			this.timeSpentSeconds = 0;
			this.$store.dispatch('prescriptions/clearSearchedPrescriptions');
			this.$store.commit('SET_DETAILS_DRAWER', false);
			// this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},

		async getPrescriptionsForPatient () {
			try {
				await this.$store.dispatch('prescriptions/getPrescriptionsByPatientId', this.detailedUser.userData.id);
			} catch (error) {
				this.$store.commit('alerts/add', {
					id: 'addedMedicalNote',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			}
		},

		async searchForPrescriptions () {
			this.loading = true;
			try {
				const searchBody = {
					drugName: this.searchText,
				};
				await this.$store.dispatch('prescriptions/fdaSearch', searchBody);
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedMedicalNote',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			}
			this.selectedPrescription = null;
			this.loading = false;
		},

		async addMedicalNote () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.currentRecord.patientId,
				hospitalId: this.currentRecord.hospitalId,
				noteDate: new Date().toISOString(),
				noteTitle: this.selectedNoteTitleOption,
				noteContent: this.noteContent,
				medicalNoteType: this.selectedNoteTypeOption,
				performedBy: this.performedBy.fullName,
				role: this.roleList.find((item) => item.id === this.performedBy.roleId).roleName,
				staffId: this.userData.id,
				timeSpentSeconds: this.timeSpentSeconds,
				additionalData: '',
			};
			try {
				await this.$store.dispatch('ccmMedicalNote/addCCMMedicalNote', body);
				await this.$store.dispatch('ccmMedicalNote/getCCMMedicalNoteByPatientId', this.currentRecord.patientId);
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedMedicalNote',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				await this.storeCCMRecordsByPatientId(this.currentRecord.patientId);
				this.closeDrawer();
			}
		},

		filterMedicalStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => (member.roleId === 3 || member.roleId === 4) && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},
	},
	async mounted () {
		this.medicalStaff = this.filterMedicalStaff();

		const responseMedicalNoteTitleOption = await this.getMedicalNoteTitleOption();
		this.noteTitleOptions = responseMedicalNoteTitleOption.data;

		const responseMedicalNoteTypeOption = await this.getMedicalNoteTypeOption();
		this.noteTypeOptions = responseMedicalNoteTypeOption.data;
	},
};
</script>
